/**
 * cp1025 — Beratertage: vereinbart, verbraucht, übrig.
 *
 * Zeigt ausschließlich Tage. Die Tagessätze sind individuell und werden in
 * OpenProject geführt; Betraege gehören deshalb nicht in diese Ansicht.
 *
 * Drei Bereiche:
 *   1. Abschmelzung  — Budget, Verbrauch, Rest, Reichweite
 *   2. Kalender      — Termine aus dem Teamkalender als Vorschlag, mit Annahme
 *   3. Aufwände     — Tage je Intervention, damit eine Vorausschau möglich wird
 *
 * Die Ansicht sagt offen, was sie nicht weiss. Eine Zahl ohne Grundlage wäre
 * im Steuerkreis schlimmer als eine ehrliche Luecke.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type Übersicht = {
  stand: string | null;
  budget_tage: number;
  budget_quelle?: string | null;
  verbraucht_tage: number;
  rest_tage: number;
  auslastung_prozent: number | null;
  stunden_je_tag: number;
  eintraege: number;
  je_ressort_tage: Record<string, number>;
  je_person_stunden: Record<string, number>;
  je_aktivitaet_stunden: Record<string, number>;
  schnitt_tage_pro_monat: number | null;
  reichweite_monate: number | null;
  arbeitspakete_mit_schaetzung: number;
  arbeitspakete_gesamt: number;
  interventionen_mit_aufwand: number;
  katalog_aufwand_tage: number;
  hinweise: string[];
};

type Vorschlag = {
  id: string; titel: string; beginn: string; ende: string;
  stunden: number; tage: number; teilnehmer: number;
  ressort_vorschlag: string | null; aktivitaet_vorschlag: string;
  status: "offen" | "angenommen" | "abgelehnt";
  arbeitspaket?: number | null; entschieden_von?: string | null;
  gebucht_in_openproject?: boolean;
};

type Kalender = {
  stand: string | null;
  kalender: Array<{ id: number; name: string }>;
  vorschlaege: Vorschlag[];
  offen: number; angenommen: number; abgelehnt: number;
  gebucht_in_openproject: number;
  offen_tage: number; angenommen_tage: number;
  schreibweg_offen: boolean;
  hinweise: string[];
};

type Aufwände = {
  interventionen: Array<{
    code: string; name: string; kategorie: string | null;
    ebene: string | null; phase: string | null;
    tage: number | null; notiz: string | null;
  }>;
  gesamt: number; mit_aufwand: number; ohne_aufwand: number; summe_tage: number;
};

function tag(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

function datum(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("de-DE",
    { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function BeratertagePanel() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [reiter, setReiter] = useState<"abschmelzung" | "kalender" | "aufwaende">("abschmelzung");
  const [suche, setSuche] = useState("");
  const [wpEingabe, setWpEingabe] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);

  const ueb = useQuery({
    queryKey: ["ber-ueb", effectiveTenantId],
    queryFn: () => api.get<Übersicht>("/beratung/uebersicht"),
  });
  const kal = useQuery({
    queryKey: ["ber-kal", effectiveTenantId],
    queryFn: () => api.get<Kalender>("/beratung/kalender"),
  });
  const auf = useQuery({
    queryKey: ["ber-auf", effectiveTenantId],
    queryFn: () => api.get<Aufwände>("/beratung/aufwaende"),
    enabled: reiter === "aufwaende",
  });

  const entscheiden = useMutation({
    mutationFn: (b: Record<string, unknown>) => api.post("/beratung/kalender/entscheiden", b),
    onSuccess: (_d, b) => {
      setMeldung(b.status === "offen" ? "Zurückgesetzt." :
        b.status === "abgelehnt" ? "Vorschlag verworfen." :
          "Angenommen. Gebucht wird erst, wenn der Schreibweg freigegeben ist.");
      qc.invalidateQueries({ queryKey: ["ber-kal"] });
      qc.invalidateQueries({ queryKey: ["ber-ueb"] });
    },
    onError: (e: any) => setMeldung(e?.details?.detail ?? "Das hat nicht geklappt."),
  });

  const aufwandSetzen = useMutation({
    mutationFn: (b: Record<string, unknown>) => api.post("/beratung/aufwand", b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ber-auf"] });
      qc.invalidateQueries({ queryKey: ["ber-ueb"] });
    },
  });

  const budgetSetzen = useMutation({
    mutationFn: (b: Record<string, unknown>) => api.post("/beratung/budget", b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ber-ueb"] }); setMeldung("Budget aktualisiert."); },
  });

  const d = ueb.data;
  const balken = d && d.budget_tage > 0
    ? Math.min(100, Math.round((d.verbraucht_tage / d.budget_tage) * 1000) / 10) : 0;

  const gefiltert = useMemo(() => {
    const rows = auf.data?.interventionen ?? [];
    const q = suche.trim().toLowerCase();
    if (!q) return rows.slice(0, 60);
    return rows.filter(r =>
      (r.code ?? "").toLowerCase().includes(q) ||
      (r.name ?? "").toLowerCase().includes(q) ||
      (r.kategorie ?? "").toLowerCase().includes(q)).slice(0, 60);
  }, [auf.data, suche]);

  return (
    <section className="sk-card" style={{ marginTop: 18 }}>
      <h3>Beratertage</h3>
      <p className="sk-sub">
        Wie viele der vereinbarten Beratertage sind verbraucht, wofür, und wie weit reicht der Rest.
        Angezeigt werden Tage — die Tagessätze sind individuell und bleiben in OpenProject.
      </p>

      <div className="sk-tabs" style={{ display: "flex", gap: 8, margin: "12px 0" }}>
        {([["abschmelzung", "Abschmelzung"],
           ["kalender", `Kalender${kal.data ? ` (${kal.data.offen} offen)` : ""}`],
           ["aufwaende", "Aufwände je Intervention"]] as const).map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setReiter(k); setMeldung(null); }}
            className={reiter === k ? "sk-chip aktiv" : "sk-chip"}
            style={{ cursor: "pointer", fontWeight: reiter === k ? 600 : 400 }}>{l}</button>
        ))}
      </div>

      {meldung ? <p className="small" style={{ margin: "0 0 10px" }}>{meldung}</p> : null}

      {/* ---------- Abschmelzung ---------- */}
      {reiter === "abschmelzung" ? (
        ueb.isLoading ? <p className="small muted">Wird geladen …</p> :
        ueb.isError ? <p className="small muted">Die Übersicht liess sich nicht laden.</p> :
        d ? (
          <>
            <div className="sk-tiles" style={{ marginBottom: 12 }}>
              {[
                { t: "Vereinbart", v: `${tag(d.budget_tage)} Tage`, s: d.budget_quelle ?? "" },
                { t: "Verbraucht", v: `${tag(d.verbraucht_tage)} Tage`, s: `aus ${d.eintraege} Zeiteinträgen` },
                { t: "Übrig", v: `${tag(d.rest_tage)} Tage`, s: d.auslastung_prozent !== null ? `${tag(d.auslastung_prozent)} % genutzt` : "" },
                { t: "Reichweite", v: d.reichweite_monate !== null ? `${tag(d.reichweite_monate)} Monate` : "noch offen",
                  s: d.schnitt_tage_pro_monat !== null ? `bei ${tag(d.schnitt_tage_pro_monat)} Tagen/Monat` : "zu wenig Verlauf" },
              ].map(k => (
                <div className="sk-tile" key={k.t}>
                  <span className="sk-tile-k">{k.t}</span>
                  <strong style={{ fontSize: 22 }}>{k.v}</strong>
                  {k.s ? <span className="small muted">{k.s}</span> : null}
                </div>
              ))}
            </div>

            <div className="sk-bar" style={{ marginBottom: 6 }}><span style={{ width: `${balken}%` }} /></div>
            <p className="small muted" style={{ margin: "0 0 14px" }}>
              Stand {datum(d.stand)} · {tag(d.stunden_je_tag)} Stunden je Beratertag
            </p>

            {d.hinweise?.length ? (
              <ul className="small" style={{ margin: "0 0 14px", paddingLeft: 18 }}>
                {d.hinweise.map((h, i) => <li key={i} style={{ marginBottom: 4 }}>{h}</li>)}
              </ul>
            ) : null}

            {Object.keys(d.je_ressort_tage ?? {}).length ? (
              <>
                <h4 style={{ margin: "6px 0" }}>Verteilung nach Ressort</h4>
                <table className="sk-table" style={{ width: "100%", fontSize: 13, marginBottom: 12 }}>
                  <thead><tr><th>Ressort</th><th style={{ textAlign: "right" }}>Tage</th></tr></thead>
                  <tbody>
                    {Object.entries(d.je_ressort_tage).sort((a, b) => b[1] - a[1]).map(([r, t]) => (
                      <tr key={r}><td>{r}</td><td style={{ textAlign: "right" }}>{tag(t)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="small muted">
                Noch keine Verteilung nach Ressort — dafür braucht es Zeiteinträge in OpenProject.
              </p>
            )}

            <details style={{ marginTop: 10 }}>
              <summary className="small">Budget ändern</summary>
              <form style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget as HTMLFormElement);
                  budgetSetzen.mutate({
                    budget_tage: Number(f.get("tage")),
                    quelle: String(f.get("quelle") ?? "im Cockpit gesetzt"),
                  });
                }}>
                <input name="tage" type="number" step="0.5" min="0.5"
                  defaultValue={d.budget_tage} style={{ width: 110 }} aria-label="Beratertage" />
                <input name="quelle" type="text" placeholder="Grundlage, z. B. Vereinbarung"
                  defaultValue={d.budget_quelle ?? ""} style={{ minWidth: 240, flex: 1 }} aria-label="Grundlage" />
                <button type="submit" disabled={budgetSetzen.isPending}>Speichern</button>
              </form>
            </details>
          </>
        ) : null
      ) : null}

      {/* ---------- Kalender ---------- */}
      {reiter === "kalender" ? (
        kal.isLoading ? <p className="small muted">Wird geladen …</p> :
        kal.data ? (
          <>
            <p className="small muted" style={{ marginTop: 0 }}>
              Quelle: {kal.data.kalender.map(k => k.name).join(", ") || "kein passender Kalender"} ·
              Stand {datum(kal.data.stand)}
            </p>
            <p className="small" style={{ margin: "0 0 10px" }}>
              <strong>{kal.data.offen}</strong> offen ({tag(kal.data.offen_tage)} Tage) ·{" "}
              <strong>{kal.data.angenommen}</strong> angenommen ({tag(kal.data.angenommen_tage)} Tage) ·{" "}
              {kal.data.abgelehnt} verworfen
              {kal.data.schreibweg_offen
                ? " · Buchung nach OpenProject ist freigegeben"
                : " · Buchung nach OpenProject ist gesperrt, Annahmen bleiben vorgemerkt"}
            </p>
            {kal.data.hinweise?.map((h, i) => (
              <p key={i} className="small muted" style={{ margin: "0 0 8px" }}>{h}</p>
            ))}

            <table className="sk-table" style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Termin</th><th>Titel</th><th style={{ textAlign: "right" }}>Std.</th>
                  <th>Ressort</th><th>Art</th><th>Arbeitspaket</th><th>Entscheidung</th>
                </tr>
              </thead>
              <tbody>
                {kal.data.vorschlaege.map(v => (
                  <tr key={v.id} style={{ opacity: v.status === "abgelehnt" ? 0.5 : 1 }}>
                    <td style={{ whiteSpace: "nowrap" }}>{datum(v.beginn)}</td>
                    <td>{v.titel}</td>
                    <td style={{ textAlign: "right" }}>{tag(v.stunden)}</td>
                    <td>{v.ressort_vorschlag ?? "—"}</td>
                    <td>{v.aktivitaet_vorschlag}</td>
                    <td>
                      {v.status === "angenommen" ? (
                        <span>#{v.arbeitspaket}{v.gebucht_in_openproject ? " ✓" : " (vorgemerkt)"}</span>
                      ) : (
                        <input type="number" min="1" placeholder="Nr."
                          value={wpEingabe[v.id] ?? ""} style={{ width: 80 }}
                          aria-label={`Arbeitspaket für ${v.titel}`}
                          onChange={e => setWpEingabe(s => ({ ...s, [v.id]: e.target.value }))} />
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {v.status === "offen" ? (
                        <>
                          <button type="button" disabled={entscheiden.isPending || !wpEingabe[v.id]}
                            title={wpEingabe[v.id] ? "Als Aufwand übernehmen" : "Erst ein Arbeitspaket eintragen"}
                            onClick={() => entscheiden.mutate({
                              id: v.id, status: "angenommen",
                              arbeitspaket: Number(wpEingabe[v.id]),
                            })}>Annehmen</button>{" "}
                          <button type="button" disabled={entscheiden.isPending}
                            onClick={() => entscheiden.mutate({ id: v.id, status: "abgelehnt" })}>Verwerfen</button>
                        </>
                      ) : (
                        <button type="button" disabled={entscheiden.isPending}
                          onClick={() => entscheiden.mutate({ id: v.id, status: "offen" })}>Zurücknehmen</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="small muted" style={{ marginTop: 10 }}>
              Ein Termin wird erst zum Aufwand, wenn ihn jemand annimmt und einem Arbeitspaket zuordnet.
              Kalender enthalten Absagen und Platzhalter — deshalb entscheidet hier ein Mensch, nicht die Maschine.
            </p>
          </>
        ) : <p className="small muted">Keine Kalenderdaten.</p>
      ) : null}

      {/* ---------- Aufwände ---------- */}
      {reiter === "aufwaende" ? (
        auf.isLoading ? <p className="small muted">Wird geladen …</p> :
        auf.data ? (
          <>
            <p className="small" style={{ marginTop: 0 }}>
              {auf.data.mit_aufwand} von {auf.data.gesamt} Interventionen haben einen Aufwand ·
              Summe {tag(auf.data.summe_tage)} Tage
            </p>
            <p className="small muted" style={{ margin: "0 0 10px" }}>
              Diese Werte sind eine fachliche Einschätzung und lassen sich nicht aus Daten ableiten.
              Ohne sie gibt es keine Vorausschau, nur den Ist-Verbrauch.
            </p>
            <input type="search" value={suche} onChange={e => setSuche(e.target.value)}
              placeholder="Suchen nach Kürzel, Name oder Kategorie"
              aria-label="Interventionen durchsuchen"
              style={{ width: "100%", maxWidth: 420, marginBottom: 10 }} />
            <table className="sk-table" style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr><th>Kürzel</th><th>Intervention</th><th>Kategorie</th>
                  <th style={{ width: 120 }}>Tage</th></tr>
              </thead>
              <tbody>
                {gefiltert.map(r => (
                  <tr key={r.code}>
                    <td style={{ whiteSpace: "nowrap" }}>{r.code}</td>
                    <td>{r.name}</td>
                    <td>{r.kategorie ?? "—"}</td>
                    <td>
                      <input type="number" step="0.25" min="0" defaultValue={r.tage ?? ""}
                        placeholder="—" style={{ width: 90 }}
                        aria-label={`Aufwand für ${r.name}`}
                        onBlur={e => {
                          const roh = e.target.value.trim();
                          const neu = roh === "" ? null : Number(roh);
                          if (neu !== (r.tage ?? null)) {
                            aufwandSetzen.mutate({ code: r.code, tage: neu });
                          }
                        }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(auf.data.interventionen.length > gefiltert.length) ? (
              <p className="small muted" style={{ marginTop: 8 }}>
                {gefiltert.length} von {auf.data.interventionen.length} gezeigt — suchen, um weitere zu finden.
              </p>
            ) : null}
          </>
        ) : <p className="small muted">Keine Interventionen geladen.</p>
      ) : null}
    </section>
  );
}
